/**
 * Triage control, shown only to INTERNAL+ and ADMIN.
 *
 * Three exports rather than one component, because the control lives in two
 * places: the select sits in the dialog's brand-coloured header where it takes
 * the place of the read-only status chip, while the "Ei toteuteta" reason
 * editor belongs on the card background below it — a textarea on bg-primary
 * reads as a form bolted onto a banner. `useStatusTriage` owns the state the
 * two halves share.
 *
 * "Ei toteuteta" demands a reason before it will submit: a report with votes on
 * it that gets closed silently teaches people to stop reporting.
 */

import { useEffect, useState } from "react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { FEEDBACK_STATUS_META } from "@/lib/feedbackMeta";
import { cn } from "@/lib/utils";
import type { FeedbackStatus } from "@/types";

const ORDER: FeedbackStatus[] = [
  "NEW",
  "TRIAGED",
  "IN_PROGRESS",
  "DONE",
  "WONT_DO",
];

interface UseStatusTriageArgs {
  /** Null while the report is still loading. */
  status: FeedbackStatus | null;
  /** Null while the report is still loading. */
  version: number | null;
  existingNote: string | null;
  onChange: (
    status: FeedbackStatus,
    version: number,
    resolutionNote?: string | null,
  ) => Promise<{ success: boolean; message?: string | null } | null | undefined>;
}

export interface StatusTriage {
  /** The status to show as selected — the pending one if a reason is awaited. */
  value: FeedbackStatus;
  note: string;
  setNote: (note: string) => void;
  /** True while "Ei toteuteta" is picked but unconfirmed. */
  awaitingReason: boolean;
  select: (next: FeedbackStatus) => void;
  confirm: () => void;
  cancel: () => void;
}

export function useStatusTriage({
  status,
  version,
  existingNote,
  onChange,
}: UseStatusTriageArgs): StatusTriage {
  const [pending, setPending] = useState<FeedbackStatus | null>(null);
  const [note, setNote] = useState(existingNote ?? "");

  // The hook runs before the report has loaded, so the initial note is empty —
  // adopt the saved reason once it arrives.
  useEffect(() => {
    setNote(existingNote ?? "");
  }, [existingNote]);

  const apply = async (next: FeedbackStatus, resolutionNote?: string | null) => {
    if (version === null) return;

    const result = await onChange(next, version, resolutionNote);
    if (result?.success) {
      toast.success(result.message ?? "Tila päivitetty");
      setPending(null);
    } else {
      toast.error(result?.message ?? "Tilan päivitys epäonnistui");
    }
  };

  return {
    value: pending ?? status ?? "NEW",
    note,
    setNote,
    awaitingReason: pending === "WONT_DO",
    select: (next) => {
      if (next === "WONT_DO") {
        // Hold the change until a reason is written.
        setPending(next);
        return;
      }
      void apply(next);
    },
    confirm: () => void apply("WONT_DO", note.trim()),
    cancel: () => setPending(null),
  };
}

/**
 * The status control as it appears beside the header content, coloured like the
 * chip it replaces — its position carries the meaning, so it needs no visible
 * "Tila" label.
 *
 * Radix Select rather than a native one. A native select hands its option list to
 * the OS, so the menu could never be styled to match the app or the header it
 * drops out of; Radix portals a real popover we control. It also sizes its
 * trigger to the *selected* label rather than the widest option, which is what
 * the previous invisible-width-spacer trick existed to work around — so the
 * spacer, the stretched-over select and `appearance-none` are all gone with it.
 */
export function StatusTriageSelect({
  triage,
  className,
}: {
  triage: StatusTriage;
  className?: string;
}) {
  return (
    <Select
      value={triage.value}
      onValueChange={(next) => triage.select(next as FeedbackStatus)}
    >
      <SelectTrigger
        aria-label="Vaihda tila"
        title="Vaihda tila"
        className={cn(
          // Overrides the trigger's default h-10 / full-width / bordered box
          // down to the pill this replaces, and recolours the built-in chevron
          // for the brand-coloured header.
          "h-6 w-auto gap-1 rounded-full border-0 bg-white/20 px-2.5 py-0",
          "text-[11px] font-semibold text-primary-foreground",
          "transition-colors hover:bg-white/30",
          "focus:ring-2 focus:ring-white/70 focus:ring-offset-0",
          "[&>svg]:h-3 [&>svg]:w-3 [&>svg]:opacity-80",
          className,
        )}
      >
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {ORDER.map((value) => (
          <SelectItem key={value} value={value} className="text-sm">
            {FEEDBACK_STATUS_META[value].label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

/**
 * The reason editor, rendered as the first block of the card body. It carries
 * its own heading because it is no longer adjacent to the select that opens it.
 */
export function WontDoReasonPanel({ triage }: { triage: StatusTriage }) {
  if (!triage.awaitingReason) return null;

  return (
    <div className="flex flex-col gap-2 rounded-lg border border-dashed border-primary/40 p-3">
      <span className="text-xs font-semibold">Miksi tätä ei toteuteta?</span>
      <Textarea
        value={triage.note}
        onChange={(event) => triage.setNote(event.target.value)}
        rows={2}
        placeholder="Perustelu näkyy ilmoittajalle."
        aria-label="Perustelu"
      />
      <div className="flex gap-2">
        <Button
          size="sm"
          disabled={!triage.note.trim()}
          onClick={triage.confirm}
        >
          Vahvista
        </Button>
        <Button size="sm" variant="ghost" onClick={triage.cancel}>
          Peruuta
        </Button>
      </div>
    </div>
  );
}
